import { renderVoiceCopy } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { useMe } from '@/platform/auth/use-me';
import { OverviewTileGrid } from '@/platform/components/overview-tile-grid';
import { OverviewTilePicker } from '@/platform/components/overview-tile-picker';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useOverviewLayout } from '@/platform/overview/use-overview-layout';
import { useVoice } from '@/platform/voice/use-voice';

export function MitarbeiterportalHomePage() {
  usePageTitle('Übersicht');
  const me = useMe();
  const voice = useVoice();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  if (!me.data) {
    return <Navigate to="/anmelden?from=%2Fstart" replace />;
  }

  return (
    <OverviewHome
      user={me.data}
      welcome={renderVoiceCopy('portal.home.welcome', voice, { actor: me.data })}
      editing={editing}
      onEditingChange={setEditing}
      onOpen={(path) => {
        if (path.startsWith('http://') || path.startsWith('https://')) {
          window.location.assign(path);
          return;
        }
        void navigate(path);
      }}
    />
  );
}

function OverviewHome({
  user,
  welcome,
  editing,
  onEditingChange,
  onOpen,
}: {
  user: NonNullable<ReturnType<typeof useMe>['data']>;
  welcome: string;
  editing: boolean;
  onEditingChange: (value: boolean) => void;
  onOpen: (path: string) => void;
}) {
  const overview = useOverviewLayout(user);

  return (
    <section className="w-full min-w-0 space-y-8">
      <PlatformPageHeader
        title="Übersicht"
        description={welcome}
        action={
          <Button
            type="button"
            variant="outline"
            disabled={overview.isPending}
            onClick={() => onEditingChange(!editing)}
          >
            {editing ? 'Fertig' : 'Bearbeiten'}
          </Button>
        }
      />
      {overview.saveError ? (
        <p className="text-sm text-destructive" role="alert">
          {overview.saveError}
        </p>
      ) : null}
      {overview.isPending ? (
        <p className="text-sm text-muted-foreground">Übersicht wird geladen …</p>
      ) : overview.tiles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Noch keine Kachel. Über Bearbeiten wählen Sie Module und Kacheln aus.
        </p>
      ) : (
        <OverviewTileGrid
          tiles={overview.tiles}
          editing={editing}
          onEditingChange={onEditingChange}
          onOpen={onOpen}
          onRemove={(path) => overview.togglePath(path)}
          onReorder={(paths) => overview.persist(paths)}
        />
      )}
      {editing ? (
        <OverviewTilePicker
          user={user}
          selectedPaths={overview.paths}
          onToggle={(path) => overview.togglePath(path)}
        />
      ) : null}
    </section>
  );
}
